<template>
    <analytics-stat-card
        :loading="loading"
        :error="Boolean(error)"
        :difference="dataComputed.difference"
        :value="dataComputed.number?.value"
        :color="dataComputed.difference < 0 ? 'bad' : 'good'"
        :direction="dataComputed.difference < 0 ? 'down' : 'up'"
        :href="props.href"
    >
        <template v-slot:title>
            <slot name="title">Contacts Messaged</slot>
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <span>{{ dataComputed.number?.label || '' }}</span>

        <template v-slot:explainHeader>
            <slot name="explainHeader"> Contacts Messaged </slot>
        </template>
        <template v-slot:explainContent>
            <slot name="explainContent">
                <p>
                    <strong>Contacts Messaged</strong><br />
                    Gross Count of unique Contacts that have been messaged during the selected period.
                </p>

                <p>
                    <strong>Contacts Messaged %</strong><br />
                    Gross Percent of unique Contacts that have been messaged relative to the previous period.
                </p>

                <vx-button href="/contacts" size="sm" color="muted">
                    Go to Contacts
                    <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                </vx-button>
            </slot>
        </template>
    </analytics-stat-card>
</template>
<script setup>
import { outboundMessagesAnalyticsClient } from '~/services';
import { computed, watch } from 'vue';
import AnalyticsStatCard from '../../partial/AnalyticsStatCard.vue';
import { VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { calculateDifference, computeComparisonRange, metricsInputValidator } from '~/utils/analytics';
import { formatCount } from '~/components/filters';
import { useVersionedRequest } from '~/composables/useVersionedRequest';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    useGroupMinDate: {
        type: Boolean,
        default: false,
    },
    href: {
        type: String,
    },
});

const { loading, response, error, fetchData } = useVersionedRequest();

const dataComputed = computed(() => {
    const responseData = response.value?.data;

    if (!responseData?.values?.length) {
        return {
            number: undefined,
            difference: undefined,
        };
    }

    const number = responseData.values.length >= 2 ? responseData.values[1] : responseData.values[0];
    const difference = responseData.values.length >= 2 ? calculateDifference(responseData.values) : undefined;

    return {
        number: {
            value: number,
            label: formatCount(number),
        },
        difference: difference,
    };
});

watch(
    [() => props.fetchParams],
    () => {
        const params = {
            ...props.fetchParams,
            range: computeComparisonRange(
                props.fetchParams.range,
                Boolean(props.groupId) || props.useGroupMinDate,
                Boolean(props.orgUnitId)
            ),
        };

        fetchData(
            outboundMessagesAnalyticsClient.getTotalContactsMessaged(
                {
                    teamId: Spark.state.currentTeam.id,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                params
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);

defineExpose({
    value: computed(() => dataComputed.value.number?.value),
    loading,
});
</script>
